import { useState } from 'react';
import { MapPin } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import type { Address } from './types';

interface AddressCardProps {
  title: string;
  address: Address;
  onSave: (address: Address) => Promise<void>;
}

export default function AddressCard({ title, address, onSave }: AddressCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editAddress, setEditAddress] = useState<Address>(address);

  const handleEdit = () => {
    setEditAddress({ ...address });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditAddress(address);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editAddress);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof Address, value: string) => {
    setEditAddress((prev) => ({ ...prev, [field]: value }));
  };

  if (isEditing) {
    return (
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First Name"
              value={editAddress.first_name}
              onChange={(e) => updateField('first_name', e.target.value)}
            />
            <Input
              label="Last Name"
              value={editAddress.last_name}
              onChange={(e) => updateField('last_name', e.target.value)}
            />
          </div>
          <Input
            label="Company"
            value={editAddress.company || ''}
            onChange={(e) => updateField('company', e.target.value)}
          />
          <Input
            label="Address Line 1"
            value={editAddress.address_line1}
            onChange={(e) => updateField('address_line1', e.target.value)}
          />
          <Input
            label="Address Line 2"
            value={editAddress.address_line2 || ''}
            onChange={(e) => updateField('address_line2', e.target.value)}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="City"
              value={editAddress.city}
              onChange={(e) => updateField('city', e.target.value)}
            />
            <Input
              label="State"
              value={editAddress.state}
              onChange={(e) => updateField('state', e.target.value)}
            />
            <Input
              label="Postal Code"
              value={editAddress.postal_code}
              onChange={(e) => updateField('postal_code', e.target.value)}
            />
          </div>
          <Input
            label="Country"
            value={editAddress.country}
            onChange={(e) => updateField('country', e.target.value)}
          />
          <Input
            label="Phone"
            value={editAddress.phone || ''}
            onChange={(e) => updateField('phone', e.target.value)}
          />
          <div className="flex gap-2 pt-2">
            <Button variant="primary" size="sm" onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
        <Button variant="ghost" size="sm" onClick={handleEdit}>
          Edit
        </Button>
      </div>
      <div className="space-y-1 text-sm text-stone-700">
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-stone-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">{address.first_name} {address.last_name}</p>
            {address.company && <p>{address.company}</p>}
            <p>{address.address_line1}</p>
            {address.address_line2 && <p>{address.address_line2}</p>}
            <p>{address.city}, {address.state} {address.postal_code}</p>
            {address.country && address.country !== 'US' && address.country !== 'USA' && (
              <p>{address.country}</p>
            )}
            {address.phone && <p className="mt-1 text-stone-500">{address.phone}</p>}
          </div>
        </div>
      </div>
    </Card>
  );
}
